import React, { useContext, useEffect, useRef, useState } from "react";
import { ProductsContext } from "../../context/ProductsContext";
import { FaEdit, FaTrash } from "react-icons/fa";
import AddProductForm from "./AddProductForm";
import { API_BASE_URL } from "../../config/api";



const AdminProducts = () => {
  const { products, fetchProducts, setProducts } =
    useContext(ProductsContext);

  const dialogRef = useRef(null);
  const formRef = useRef(null);
  const [mode, setMode] = useState("add");
  const [editingProduct, setEditingProduct] = useState(null);
  const [search, setSearch] = useState("");

  // ================= FILTER =================
  const [filter, setFilter] = useState({
    category: "All",
    brand: "All",
    stock: "All",
    minPrice: "",
    maxPrice: "",
  });

  // ================= PAGINATION =================
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  console.log("Products from context:", products);

  // ================= FILTER LOGIC =================
const filteredProducts = (products || []).filter((p) => {
  const price = Number(p.price || 0);

  const searchText = search.toLowerCase();

  const matchesSearch =
    p.name?.toLowerCase().includes(searchText) ||
    p.brand?.toLowerCase().includes(searchText) ||
    p.model?.toLowerCase().includes(searchText);

  if (search && !matchesSearch) return false;

  if (filter.category !== "All" && p.category !== filter.category)
    return false;

  if (filter.brand !== "All" && p.brand !== filter.brand)
    return false;

  if (filter.stock === "in" && (p.stock ?? 0) <= 0) return false;
  if (filter.stock === "out" && (p.stock ?? 0) > 0) return false;

  if (filter.minPrice && price < Number(filter.minPrice)) return false;
  if (filter.maxPrice && price > Number(filter.maxPrice)) return false;

  return true;
});

  // ================= PAGINATION =================
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / itemsPerPage)
  );

  const safePage = Math.min(currentPage, totalPages);

  const paginatedProducts = filteredProducts.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // ================= IMAGE FIX =================
  const fixedProducts = paginatedProducts.map((p) => ({
    ...p,
    imageUrl:
      Array.isArray(p.images) && p.images.length > 0
        ? `${API_BASE_URL}/uploads/${p.images[0]}`
        : null,
  }));

  // ================= REMOVE PRODUCT =================
  const handleRemove = async (id) => {
    if (!window.confirm("Are you sure?")) return;

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/api/products/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Delete failed");

      await fetchProducts();
    } catch (err) {
      console.error(err);
      await fetchProducts();
    }
  };

  // ================= PUBLISH CHANGE =================
  const handlePublishChange = async (id, value) => {
    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(
        `${API_BASE_URL}/api/products/${id}/publish`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_published: Number(value), // FIX IMPORTANT
          }),
        }
      );

      if (!res.ok) throw new Error("Update failed");

      setProducts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, is_published: Number(value) } : p
        )
      );
    } catch (err) {
      console.error(err.message);
    }
  };

    const closeDialog = () => {
    dialogRef.current?.close();
    formRef.current?.resetForm();
    setEditingProduct(null);
    setMode("add");
  };

  // ================= UI =================
  return (
    <div className="bg-gray-100 text-gray-800">
      <main className="ml-64 overflow-auto">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Produktet</h1>

          <button
            onClick={() => {
              setEditingProduct(null);
              dialogRef.current?.showModal();
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            + Shto Produkt
          </button>
        </div>

        {/* SEARCH BAR */}
          <div className="mb-4 flex gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kërko produkt (emër, brand, model...)"
              className="w-full md:w-1/2 p-2 border rounded shadow-sm"
            />

            <button
              onClick={() => setSearch("")}
              className="bg-gray-200 px-4 rounded"
            >
              Reset
            </button>
          </div>

        {/* TABLE */}
        <div className="bg-white rounded-lg shadow overflow-x-auto w-full min-w-[1200px]">
          <table className="w-full text-sm">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="p-3">ID</th>
                <th className="p-3">Foto</th>
                <th className="p-3">Emri</th>
                <th className="p-3">Category</th>
                <th className="p-3">Brand</th>
                <th className="p-3">Model</th>
                <th className="p-3">Çmimi</th>
                <th className="p-3">Stock</th>
                <th className="p-3">Publikuar</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {fixedProducts.map((p) => (
                <tr key={p.id} className="border-b">
                  <td className="p-3">{p.id}</td>

                  <td className="p-3">
                    {p.imageUrl ? (
                      <img
                        src={p.imageUrl}
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded" />
                    )}
                  </td>

                  <td className="p-3 font-semibold">{p.name}</td>
                  <td className="p-3">{p.category || "-"}</td>
                  <td className="p-3">{p.brand || "-"}</td>
                  <td className="p-3">{p.model || "-"}</td>

                  <td className="p-3 font-bold text-green-600">
                    €{Number(p.price || 0).toFixed(2)}
                  </td>

                  <td className="p-3">{p.stock ?? 0}</td>

                  <td className="p-3">
                    <select
                      value={p.is_published}
                      onChange={(e) =>
                        handlePublishChange(p.id, e.target.value)
                      }
                    >
                      <option value="0">NO</option>
                      <option value="1">YES</option>
                    </select>
                  </td>

                  <td className="p-3 flex gap-2">
                    <button
                    onClick={() => {
                      setEditingProduct(p);
                      dialogRef.current?.showModal();
                    }}
                  >
                    <FaEdit />
                  </button>

                    <button onClick={() => handleRemove(p.id)}>
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex justify-center gap-4 mt-6">
          <button
            disabled={safePage === 1}
            onClick={() => goToPage(safePage - 1)}
          >
            ←
          </button>

          <span>
            {safePage} / {totalPages}
          </span>

          <button
            disabled={safePage === totalPages}
            onClick={() => goToPage(safePage + 1)}
          >
            →
          </button>
        </div>

        {/* MODAL */}
          <dialog ref={dialogRef} className="rounded-xl shadow-xl w-[600px]">
    
         <div className="p-4 border-b flex justify-end">
                   <button
                    onClick={closeDialog}
                    className="text-gray-500 hover:text-red-600 hover:bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center transition"
                  >
                    ✕
                  </button>
                  </div>
        
          <AddProductForm
            ref={formRef}
            initialData={editingProduct}
            onAdd={async (formData) => {
              try {
                const token = localStorage.getItem("authToken");

                let url = `${API_BASE_URL}/api/products`;
                let method = "POST";

                if (editingProduct?.id) {
                  url = `${API_BASE_URL}/api/products/${editingProduct.id}`;
                  method = "PUT";
                }

                const res = await fetch(url, {
                  method,
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  body: formData,
                });

                if (!res.ok) throw new Error("Save failed");

                await fetchProducts();
                dialogRef.current?.close();
                setEditingProduct(null);
              } catch (err) {
                console.error(err);
              }
            }}
          />
        </dialog>
      </main>
    </div>
  );
};

export default AdminProducts;